import {
  GoalOverviewRoadmapViewOptions,
  PageEvents,
  TimelineTimeframes,
} from '@griply/types';
import {
  buildShowCompletedGoalsUpdate,
  getShowCompletedGoals,
  GoalCompletedContext,
} from '@griply/utils';
import { useLifeAreaById } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  Plus,
  SettingsSort18,
  Table14,
  Timeline214,
} from 'shared/assets/icons';
import { TimelinePremiumLock } from 'shared/components/connected/timeline-premium-lock';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { TimeframeNavigationButtons } from 'shared/components/ui/timeframe-navigation-buttons';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { LimitKeys } from 'shared/types/limit-keys';
import { useTheme } from 'styled-components';

import { LifeAreaRoadmapAddMenu } from './components/life-area-roadmap-add-menu';
import { LifeAreaRoadmapFilterButton } from './components/life-area-roadmap-filter-button';
import { LifeAreaRoadmapOverview } from './components/life-area-roadmap-overview';
import { LifeAreaRoadmapTimeline } from './components/life-area-roadmap-timeline';
import { useLifeAreaRoadmapControls } from './hooks/use-life-area-roadmap-controls';
import * as Styled from './life-area-detail.style';

// Roadmap items are top-level in the life area, so they have no parent goal.
const NO_PARENT_IDS: ID[] = [];

export const Roadmap: React.FC = () => {
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailGoals, undefined);

  const { id } = useParams();
  const theme = useTheme();
  const lifeArea = useLifeAreaById(id);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  const {
    view,
    setView,
    timeframe,
    setTimeframe,
    referenceDate,
    scrollRequest,
    onPrev,
    onToday,
    onNext,
  } = useLifeAreaRoadmapControls(lifeArea);
  const { allowed: isTimelineAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopGoalsOverviewTimeline,
    {},
  );
  const isTimeline =
    !theme.isMobile && view === GoalOverviewRoadmapViewOptions.Timeline;
  const showTimeline = isTimeline && isTimelineAllowed;

  const viewOptions = useMemo<Option<GoalOverviewRoadmapViewOptions>[][]>(
    () => [
      [
        { icon: Table14, value: GoalOverviewRoadmapViewOptions.List },
        { icon: Timeline214, value: GoalOverviewRoadmapViewOptions.Timeline },
      ],
    ],
    [],
  );

  const timeframeOptions = useMemo<Option<TimelineTimeframes>[][]>(
    () => [
      [
        {
          label: t('general.labels.timeframes.month.short'),
          value: TimelineTimeframes.Month,
        },
        {
          label: t('general.labels.timeframes.quarter.short'),
          value: TimelineTimeframes.Quarter,
        },
        {
          label: t('general.labels.timeframes.year.short'),
          value: TimelineTimeframes.Year,
        },
        {
          label: t('general.labels.timeframes.five-years.short'),
          value: TimelineTimeframes.FiveYears,
        },
      ],
    ],
    [t],
  );

  const showCompletedGoals = getShowCompletedGoals(
    user?.settings,
    GoalCompletedContext.LifeAreaDetail,
  );

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings(
      buildShowCompletedGoalsUpdate(GoalCompletedContext.LifeAreaDetail, value),
    );
  };

  if (!lifeArea) {
    return null;
  }

  const handleOpenAddMenu = () => setIsAddMenuOpen(true);
  const handleCloseAddMenu = () => setIsAddMenuOpen(false);
  const handleOpenSortMenu = () => setIsSortMenuOpen(true);
  const handleCloseSortMenu = () => setIsSortMenuOpen(false);

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.tabs.roadmap.label')}
      fullSize
      noPadding
      centerInteractions={
        theme.isMobile ? undefined : (
          <>
            <SliderButtons
              optionGroups={viewOptions}
              onOption={setView}
              value={view}
              preventDrag
            />
            {showTimeline && (
              <>
                <SliderButtons
                  optionGroups={timeframeOptions}
                  onOption={setTimeframe}
                  value={timeframe}
                  preventDrag
                />
                <TimeframeNavigationButtons
                  onPrev={onPrev}
                  onToday={onToday}
                  onNext={onNext}
                />
              </>
            )}
          </>
        )
      }
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={Plus}
            ref={addButtonRef}
            onClick={handleOpenAddMenu}
            color={Colors.Default}
          />
          <LifeAreaRoadmapFilterButton lifeArea={lifeArea} />
          <IconButton
            icon={SettingsSort18}
            onClick={handleOpenSortMenu}
            color={Colors.Default}
          />
          {isAddMenuOpen && (
            <PopupMenu
              referenceElement={addButtonRef}
              onOutside={handleCloseAddMenu}
              position="bottom-end"
            >
              <LifeAreaRoadmapAddMenu
                parentIds={NO_PARENT_IDS}
                lifeAreaId={lifeArea.id}
                onClose={handleCloseAddMenu}
              />
            </PopupMenu>
          )}
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={handleCloseSortMenu}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
    >
      {!isTimeline && (
        <LifeAreaRoadmapOverview
          lifeArea={lifeArea}
          hideCompleted={!showCompletedGoals}
          fullSize
        />
      )}
      {isTimeline &&
        (isTimelineAllowed ? (
          <LifeAreaRoadmapTimeline
            lifeArea={lifeArea}
            timeframe={timeframe}
            referenceDate={referenceDate}
            scrollRequest={scrollRequest}
            hideCompleted={!showCompletedGoals}
          />
        ) : (
          <TimelinePremiumLock
            title={t('pages.life-area-detail.tabs.roadmap.label')}
          />
        ))}
    </PageSectionBase>
  );
};
